import { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Flex,
  Icon,
  Popover,
  Portal,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MdStar as StarIcon, MdArrowBack as BackIcon, MdClose as ClearIcon } from 'react-icons/md';
import { useTagGroups } from '@/lib/recipeService';
import { FILTER_EXCLUDED_TAG_GROUPS } from '@/constants/tags';

const RATING_OPTIONS = [
  { label: '2+', value: 2 },
  { label: '3+', value: 3 },
  { label: '4+', value: 4 },
  { label: '5★', value: 5 },
];

const GROUP_COLORS: Record<string, string> = {
  Rating: '#c85a1e',
  Cuisine: '#366b7d',
  Diet: '#729e6b',
  Meal: '#b4adb2',
  Course: '#d97706',
};
const DEFAULT_GROUP_COLOR = '#8a7f76';

function groupColor(name: string) {
  return GROUP_COLORS[name] ?? DEFAULT_GROUP_COLOR;
}

type PickerState =
  | null
  | { source: 'add'; step: 'category' }
  | { source: 'add'; step: 'values'; group: string }
  | { source: 'chip'; group: string };

function activePickerGroup(picker: PickerState): string | null {
  if (!picker) return null;
  if (picker.source === 'chip') return picker.group;
  if (picker.source === 'add' && picker.step === 'values') return picker.group;
  return null;
}

export interface FilterSheetProps {
  selectedTags: string[];
  onToggleTag: (tag: string, group: string) => void;
  selectedRatings: number[];
  onToggleRating: (value: number) => void;
  onClear: () => void;
  totalActive: number;
  triggerAddFilter?: number;
}

export default function FilterSheet({
  selectedTags,
  onToggleTag,
  selectedRatings,
  onToggleRating,
  onClear,
  totalActive,
  triggerAddFilter,
}: FilterSheetProps) {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [picker, setPicker] = useState<PickerState>(null);

  useEffect(() => {
    if (triggerAddFilter) {
      setPicker({ source: 'add', step: 'category' });
    }
  }, [triggerAddFilter]);

  const { tagGroups, isLoading } = useTagGroups({ exclude: FILTER_EXCLUDED_TAG_GROUPS });
  const activeTagGroups = tagGroups.filter(g => g.tags.some(t => selectedTags.includes(t)));
  const showClear = totalActive > 0;

  const closePicker = () => setPicker(null);
  const openChipPicker = (group: string) => setPicker({ source: 'chip', group });
  const selectCategory = (group: string) => setPicker({ source: 'add', step: 'values', group });
  const backToCategory = () => setPicker({ source: 'add', step: 'category' });

  const clearGroup = (groupName: string) => {
    if (groupName === 'Rating') {
      selectedRatings.forEach(v => onToggleRating(v));
    } else {
      tagGroups
        .find(g => g.name === groupName)
        ?.tags.filter(t => selectedTags.includes(t))
        .forEach(t => onToggleTag(t, groupName));
    }
  };

  // ── Chip label for an active group ──────────────────────────────────────
  const chipValueLabel = (groupName: string, groupTags: string[]) => {
    if (groupName === 'Rating') {
      const sorted = [...selectedRatings].sort((a, b) => a - b);
      const first = sorted[0] === 5 ? '5★' : `${sorted[0]}+`;
      const extra = sorted.length - 1;
      return extra > 0 ? `${first} +${extra}` : first;
    }
    const active = groupTags.filter(t => selectedTags.includes(t));
    const extra = active.length - 1;
    return extra > 0 ? `${active[0]} +${extra}` : active[0];
  };

  // ── Value list content (shared by popover + drawer) ──────────────────────
  const renderValueList = (group: string) => {
    if (group === 'Rating') {
      return (
        <Flex direction="column">
          {RATING_OPTIONS.map(({ label, value }) => {
            const active = selectedRatings.includes(value);
            return (
              <Flex
                key={value}
                as="button"
                align="center"
                gap={3}
                px={4}
                py="10px"
                cursor="pointer"
                onClick={() => onToggleRating(value)}
                bg={active ? 'accent.secondarySubtle' : 'transparent'}
                _hover={{ bg: active ? 'accent.secondarySubtle' : 'bg.surface' }}
                borderBottom="1px solid"
                borderColor="border.muted"
                _last={{ borderBottom: 'none' }}
                textAlign="left"
              >
                <Box
                  w="15px"
                  h="15px"
                  borderRadius="4px"
                  border="1.5px solid"
                  bg={active ? '#c85a1e' : 'transparent'}
                  borderColor={active ? '#c85a1e' : 'border.muted'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  {active && <Box w="7px" h="7px" borderRadius="2px" bg="white" />}
                </Box>
                <Flex align="center" gap="4px">
                  <Icon as={StarIcon} boxSize="12px" color="text.muted" />
                  <Text fontFamily="body" fontSize="15px" color="text.primary">
                    {label}
                  </Text>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      );
    }

    const tagGroup = tagGroups.find(g => g.name === group);
    if (!tagGroup) return null;
    const color = groupColor(group);
    return (
      <Flex direction="column" maxH="260px" overflowY="auto">
        {tagGroup.tags.map(tag => {
          const active = selectedTags.includes(tag);
          return (
            <Flex
              key={tag}
              as="button"
              align="center"
              gap={3}
              px={4}
              py="10px"
              cursor="pointer"
              onClick={() => onToggleTag(tag, group)}
              bg={active ? 'accent.secondarySubtle' : 'transparent'}
              _hover={{ bg: active ? 'accent.secondarySubtle' : 'bg.surface' }}
              borderBottom="1px solid"
              borderColor="border.muted"
              _last={{ borderBottom: 'none' }}
              textAlign="left"
            >
              <Box
                w="15px"
                h="15px"
                borderRadius="4px"
                border="1.5px solid"
                bg={active ? color : 'transparent'}
                borderColor={active ? color : 'border.muted'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                {active && <Box w="7px" h="7px" borderRadius="2px" bg="white" />}
              </Box>
              <Text fontFamily="body" fontSize="15px" color="text.primary">
                {tag}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  // ── Category list ────────────────────────────────────────────────────────
  const renderCategoryList = () => {
    const allGroups = [{ name: 'Rating', tags: [] as string[] }, ...tagGroups];
    return (
      <Flex direction="column">
        {isLoading ? (
          <Flex justify="center" py={4}>
            <Spinner size="sm" color="accent.secondary" />
          </Flex>
        ) : (
          allGroups.map(g => (
            <Flex
              key={g.name}
              as="button"
              align="center"
              gap={3}
              px={4}
              py="10px"
              cursor="pointer"
              onClick={() => selectCategory(g.name)}
              _hover={{ bg: 'bg.surface' }}
              borderBottom="1px solid"
              borderColor="border.muted"
              _last={{ borderBottom: 'none' }}
              textAlign="left"
            >
              <Box w="6px" h="6px" borderRadius="full" bg={groupColor(g.name)} flexShrink={0} />
              <Text fontFamily="body" fontSize="15px" color="text.primary">
                {g.name}
              </Text>
              <Box ml="auto" color="text.muted" fontSize="14px" lineHeight="1">
                ›
              </Box>
            </Flex>
          ))
        )}
      </Flex>
    );
  };

  // ── Picker header (popover + drawer) ────────────────────────────────────
  const renderPickerHeader = (title: string, color?: string, onBack?: () => void) => (
    <Flex
      align="center"
      gap={2}
      px={4}
      py="10px"
      borderBottom="1px solid"
      borderColor="border.muted"
      flexShrink={0}
    >
      {onBack && (
        <Box
          as="button"
          onClick={onBack}
          color="text.muted"
          display="flex"
          alignItems="center"
          cursor="pointer"
          mr={1}
          _hover={{ color: 'text.primary' }}
        >
          <Icon as={BackIcon} boxSize="16px" />
        </Box>
      )}
      {color && <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} />}
      <Text
        fontFamily="heading"
        fontWeight="700"
        fontSize="10px"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="text.muted"
      >
        {title}
      </Text>
    </Flex>
  );

  // ── Active chip visual ────────────────────────────────────────────────────
  const chipVisual = (groupName: string, groupTags: string[]) => {
    const color = groupColor(groupName);
    const label =
      groupName === 'Rating'
        ? `★ ${chipValueLabel(groupName, groupTags)}`
        : chipValueLabel(groupName, groupTags);
    return (
      <Flex
        as="span"
        align="center"
        h="24px"
        borderRadius="6px"
        border="1px solid"
        borderColor={color}
        overflow="hidden"
        flexShrink={0}
      >
        <Box
          px="6px"
          h="100%"
          bg={color}
          display="flex"
          alignItems="center"
          fontSize="9px"
          fontWeight="700"
          fontFamily="heading"
          letterSpacing="0.04em"
          textTransform="uppercase"
          color="white"
          flexShrink={0}
        >
          {groupName}
        </Box>
        <Flex
          align="center"
          gap={1}
          px="6px"
          h="100%"
          fontSize="11px"
          fontFamily="body"
          color={color}
          fontWeight="600"
        >
          {label}
          <Box
            role="button"
            tabIndex={0}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              clearGroup(groupName);
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                clearGroup(groupName);
              }
            }}
            opacity={0.6}
            _hover={{ opacity: 1 }}
            fontSize="12px"
            lineHeight="1"
            color={color}
            cursor="pointer"
          >
            ×
          </Box>
        </Flex>
      </Flex>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // MOBILE
  // ═══════════════════════════════════════════════════════════
  if (isMobile) {
    const drawerGroup = activePickerGroup(picker);
    const isCategory = picker?.source === 'add' && picker.step === 'category';

    return (
      <>
        {/* Active chip row — only shown when filters are active */}
        {showClear && (
          <Box>
            <Flex align="center" gap={2} px={4} pb={3} flexWrap="wrap">
              {selectedRatings.length > 0 && (
                <Box as="button" cursor="pointer" onClick={() => openChipPicker('Rating')}>
                  {chipVisual('Rating', [])}
                </Box>
              )}
              {activeTagGroups.map(g => (
                <Box
                  key={g.name}
                  as="button"
                  cursor="pointer"
                  onClick={() => openChipPicker(g.name)}
                >
                  {chipVisual(g.name, g.tags)}
                </Box>
              ))}
              <Box
                as="button"
                aria-label="Clear all filters"
                onClick={onClear}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="24px"
                h="24px"
                borderRadius="full"
                border="1px solid"
                borderColor="accent.secondary"
                color="accent.secondary"
                cursor="pointer"
                flexShrink={0}
                _hover={{ opacity: 0.75 }}
              >
                <Icon as={ClearIcon} boxSize="12px" />
              </Box>
            </Flex>
          </Box>
        )}

        {/* Drawer */}
        <Drawer.Root
          open={picker !== null}
          onOpenChange={e => {
            if (!e.open) closePicker();
          }}
          placement="bottom"
        >
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content
                roundedTop="2xl"
                maxH="70vh"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 20px)' }}
              >
                <Box
                  w="36px"
                  h="4px"
                  borderRadius="2px"
                  bg="border.muted"
                  mx="auto"
                  mt={3}
                  mb={1}
                />

                {isCategory ? (
                  <>
                    <Drawer.Header borderBottom="1px solid" borderColor="border.muted" py="10px">
                      <Drawer.Title asChild>
                        <Text
                          fontFamily="heading"
                          fontWeight="700"
                          fontSize="10px"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          color="text.muted"
                        >
                          Add a filter
                        </Text>
                      </Drawer.Title>
                    </Drawer.Header>
                    <Drawer.Body p={0} overflowY="auto">
                      {renderCategoryList()}
                    </Drawer.Body>
                  </>
                ) : drawerGroup ? (
                  <>
                    <Drawer.Header borderBottom="1px solid" borderColor="border.muted" py="10px">
                      <Flex align="center" gap={2}>
                        {picker?.source === 'add' && (
                          <Box
                            as="button"
                            aria-label="Back to categories"
                            onClick={backToCategory}
                            color="text.muted"
                            display="flex"
                            alignItems="center"
                            cursor="pointer"
                            mr={1}
                            _hover={{ color: 'text.primary' }}
                          >
                            <Icon as={BackIcon} boxSize="16px" />
                          </Box>
                        )}
                        <Box
                          w="6px"
                          h="6px"
                          borderRadius="full"
                          bg={groupColor(drawerGroup)}
                          flexShrink={0}
                        />
                        <Drawer.Title asChild>
                          <Text
                            fontFamily="heading"
                            fontWeight="700"
                            fontSize="10px"
                            letterSpacing="0.1em"
                            textTransform="uppercase"
                            color="text.muted"
                          >
                            {drawerGroup}
                          </Text>
                        </Drawer.Title>
                      </Flex>
                    </Drawer.Header>
                    <Drawer.Body p={0} overflowY="auto">
                      {renderValueList(drawerGroup)}
                    </Drawer.Body>
                  </>
                ) : null}
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // DESKTOP
  // ═══════════════════════════════════════════════════════════
  return (
    <Box bg="bg.canvas">
      <Flex
        align="center"
        gap={2}
        px={4}
        py={2}
        overflowX="auto"
        css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {/* Active chip — Rating */}
        {selectedRatings.length > 0 && (
          <Popover.Root
            open={picker?.source === 'chip' && picker.group === 'Rating'}
            onOpenChange={e => {
              if (!e.open) closePicker();
            }}
            positioning={{ placement: 'bottom-start' }}
            lazyMount
            unmountOnExit
          >
            <Popover.Trigger asChild>
              <Box
                cursor="pointer"
                onClick={() =>
                  picker?.source === 'chip' && picker.group === 'Rating'
                    ? closePicker()
                    : openChipPicker('Rating')
                }
              >
                {chipVisual('Rating', [])}
              </Box>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content p={0} minW="190px" overflow="hidden">
                  {renderPickerHeader('Rating', groupColor('Rating'))}
                  {renderValueList('Rating')}
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        )}

        {/* Active chips — tag groups */}
        {activeTagGroups.map(group => {
          const isOpen = picker?.source === 'chip' && picker.group === group.name;
          return (
            <Popover.Root
              key={group.name}
              open={isOpen}
              onOpenChange={e => {
                if (!e.open) closePicker();
              }}
              positioning={{ placement: 'bottom-start' }}
              lazyMount
              unmountOnExit
            >
              <Popover.Trigger asChild>
                <Box
                  cursor="pointer"
                  onClick={() => (isOpen ? closePicker() : openChipPicker(group.name))}
                >
                  {chipVisual(group.name, group.tags)}
                </Box>
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content p={0} minW="190px" overflow="hidden">
                    {renderPickerHeader(group.name, groupColor(group.name))}
                    {renderValueList(group.name)}
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
          );
        })}

        {/* Clear all */}
        {showClear && (
          <Text
            ml="auto"
            fontFamily="body"
            fontWeight="600"
            fontSize="12px"
            color="text.muted"
            cursor="pointer"
            onClick={onClear}
            _hover={{ color: 'text.primary' }}
            whiteSpace="nowrap"
            flexShrink={0}
          >
            Clear all
          </Text>
        )}
      </Flex>
    </Box>
  );
}
